import * as React from 'react';

import { IDispatcher } from '../libs/helpers/Dispatcher';
import { I18NString } from '../libs/helpers/Utils';

/* Controls */
import { createCalendar } from '../libs/controls/Calendar';
import { createCheckbox } from '../libs/controls/Checkbox';
import { createDropBox } from '../shared/ui/DropBox';
import { createObjectControl } from '../libs/controls/ObjectControl';
import { createRadiobutton } from '../libs/controls/Radiobutton';
import { createTextBox } from '../libs/controls/TextBox';
// import { createGrid } from "../libs/controls/Grid";
import { createCheckBoxSet } from '../libs/controls/CheckBoxSet';
import { createComponentProperties } from '../libs/controls/ComponentProperties';
import { createDynamicGrid } from '../libs/controls/DynamicGridPanel';
import { createFixedTable } from '../libs/controls/FixedTable';
import { createGridEditable } from '../libs/controls/Grid/GridEditable/GridEditable';
import { createGroup } from '../libs/controls/Group';
import { createLabel } from '../libs/controls/Label';
import { createMultiFiles } from '../libs/controls/MultipleFiles';
// TODO:!!!
import { createFiles } from '../libs/controls/Files/Files';
import { createPanel } from '../libs/controls/Panel';
import { createSelectBlock } from '../libs/controls/SelectBlock';
import { createServiceTable } from '../libs/controls/ServiceTable';
import { createSigCreatorFromFile } from '../libs/controls/SigCreatorFromFile';
import { createTelephone } from '../libs/controls/Telephone';
import { createTkpLinks } from '../libs/controls/TkpLinks';
import { createTotalHeader } from '../libs/controls/TotalHeader';
import { createButtonSet } from '../libs/controls/ButtonSet';
import { createGrid } from '../libs/controls/Grid';
import { createWorkExperienceList } from '../libs/controls/WorkExperienceList';

import { GridSchema } from '../libs/controls/Grid/interfaces';

import { Dictionary } from '../types/Dictionary';

// eslint-disable-next-line no-shadow
export enum RuleOptions {
  NotEqual = 'NotEqual',
  Equal = 'Equal',
}

type ControlTypes =
  | 'TEXTBOX'
  | 'TEXTAREA'
  | 'NUMBER'
  | 'FLOAT'
  | 'FILE'
  | 'OBJECT'
  | 'SERVICETABLE'
  | 'CALENDAR'
  | 'RANGEDMULTICALENDAR'
  | 'CHECKBOX'
  | 'RADIOBUTTON'
  | 'CHECKBOXSET'
  | 'LABEL'
  | 'TELEPHONE'
  | 'FIXEDTABLE'
  | 'SELECTBLOCK'
  | 'COMPONENTPROPERTIES'
  | 'TKPLINKS'
  | 'SIGCREATORFROMFILE'
  | 'BUTTONSET'
  | 'WORKEXPERIENCELIST';

export interface IControlError {
  isError: boolean;
  message: any;
  isFrontValidation?: boolean;
}

export interface ControlErrorListItem extends IControlError {
  controlId: string;
}

export interface IControlSchema {
  viewType?: string;
  urlParams?: { [p: string]: string };
  fixedValueWithCalculate: any;
  controls?: Array<any>;
  id: string;
  type: string;
  contentNumb: string;
  disableNumber?: boolean;
  title: I18NString;
  required: boolean;
  viewableFor?: Array<string>;
  visible?: boolean;
  rule?: {
    targetInColumn: any;
    controlTargetId: string;
    option: any;
    value: string;
  };
  requiredRule?: {
    type: 'OR' | 'AND';
    rules: [
      {
        controlTargetId: string;
        option: any;
        value: string;
      },
    ];
  };
  visibleRule?: {
    type: 'OR' | 'AND';
    rules: [
      {
        controlTargetId: string;
        option: any;
        value: string;
      },
    ];
  };
  fill_rule?: {
    value: any;
    option: any;
    controlTargetId: any;
    controlSourceId: any;
  };
  rule_operation?: string;
  disabled?: boolean;
  defaultValue?: any;
  fixedValue?: any;
  countries?: Array<any>;
  flows?: Array<any>;
  changed?: any;
  columns?: Array<any>;
  extra?: any;
  value?: any;
  controlValue?: any;
  minLength?: number;
  maxLength?: number;
  blockedIfEmpty?: {
    id: string;
    errorMessage: string;
  };
  valueFromField?: {
    from: {
      [key: string]: {
        field: string;
        grid?: string;
        type: string | number;
      };
    };
    result: string;
  };
  widget?: {
    selectType?: string;
    dynamicType?: string;
  };
  template?: {
    templateTitle: string;
    columns?: Array<any>;
  };
  isPortal?: boolean;
}

export interface ValidationResult {
  message: string;
}

export interface IControlCreateHandler {
  (
    schema: IControlSchema,
    api: unknown,
    dispatcher: IDispatcher,
    error: IControlError | undefined | Dictionary<IControlError | undefined>,
    visited: boolean | Dictionary<boolean>,
    opts?: any,
    mask?: string,
    maskChar?: string,
    size?: number,
  ): React.ReactElement<any>;
}

const _controls: Record<ControlTypes, IControlCreateHandler> = {
  TEXTBOX: createTextBox,
  TEXTAREA: createTextBox,
  NUMBER: createTextBox,
  FLOAT: createTextBox,
  FILE: createMultiFiles,
  OBJECT: createObjectControl,
  SERVICETABLE: createServiceTable,
  CALENDAR: createCalendar,
  RANGEDMULTICALENDAR: createCalendar,
  CHECKBOX: createCheckbox,
  RADIOBUTTON: createRadiobutton,
  CHECKBOXSET: createCheckBoxSet,
  LABEL: createLabel,
  TELEPHONE: createTelephone,
  FIXEDTABLE: createFixedTable,
  SELECTBLOCK: createSelectBlock,
  COMPONENTPROPERTIES: createComponentProperties,
  TKPLINKS: createTkpLinks,
  SIGCREATORFROMFILE: createSigCreatorFromFile,
  BUTTONSET: createButtonSet,
  WORKEXPERIENCELIST: createWorkExperienceList,
};

// TODO: remove callback(...args[]) and replace with callback({...args -> Record<string, TYPE>})
export function createControl(
  schema: IControlSchema,
  api: unknown,
  dispatcher: IDispatcher,
  errors: Dictionary<IControlError | undefined>,
  values: any,
  visited: Dictionary<boolean>,
  allVisited: boolean,
  opts?: any,
  information?: any,
  allSections?: unknown,
  dynamicOptions?: any,
  rowIndex?: number,
  remoteControls?: {
    [key: string]: (...args: any) => any;
  },
): React.ReactElement<any> {
  const type = schema.type.toUpperCase();
  const viewType = schema.viewType?.toUpperCase();

  const resultType = viewType ? viewType : type;
  switch (resultType) {
    case '_FILE':
      return createFiles({
        api,
        schema,
        error: errors?.[schema.id],
        dispatcher,
      });
    case 'GRIDEDITABLE':
      return createGridEditable({
        api,
        values,
        schema: schema as GridSchema,
        dispatcher,
        error: errors?.[schema.id],
      });
    case 'PANEL':
      return createPanel(
        schema,
        api,
        dispatcher,
        errors,
        values,
        visited,
        allVisited,
      );
    case 'DYNAMICGRIDPANEL':
      return createDynamicGrid(
        schema,
        api,
        dispatcher,
        errors,
        values,
        visited,
        allVisited,
      );
    case 'TOTALHEADER':
      return createTotalHeader(schema, api, dispatcher, values);
    case 'GROUP':
      return createGroup(
        schema,
        api,
        dispatcher,
        errors,
        values,
        visited,
        opts,
        allVisited,
      );
    case 'GRID':
      return createGrid(
        schema,
        api,
        dispatcher,
        errors?.[schema.id],
        allVisited || visited?.[schema.id],
        opts,
        information,
        values,
        rowIndex,
      );
    case 'FIXEDTABLE':
      return createFixedTable(
        schema,
        api,
        dispatcher,
        errors?.[schema.id],
        allVisited || visited?.[schema.id],
        allSections,
      );
    case 'DROPBOX':
      return createDropBox(
        schema,
        api,
        dispatcher,
        errors?.[schema.id],
        allVisited || visited?.[schema.id],
        dynamicOptions,
      );
    case 'TEXTBOX':
    case 'TEXTAREA':
    case 'NUMBER':
    case 'FLOAT':
      return createTextBox(schema, api, dispatcher, errors, visited);
    default: {
      if (remoteControls && Object.keys(remoteControls).includes(resultType)) {
        return remoteControls[resultType](
          schema,
          api,
          dispatcher,
          errors,
          visited,
          opts,
          information,
          values,
          rowIndex,
          allSections,
        );
      }

      return _controls?.[resultType](
        schema,
        api,
        dispatcher,
        errors?.[schema.id],
        visited?.[schema.id] || allVisited,
        opts,
      );
    }
  }
}
